import { Capacitor } from '@capacitor/core';
import * as Sentry from '@sentry/vue';
import { ref } from 'vue';

const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3
};

// Rate limiting configuration
const ERROR_RATE_LIMITS = {
  'JWT_CLAIM': 1800000 // 30 minutes in milliseconds
};

// Map to store last logged timestamps for rate-limited errors
const lastErrorLogTimes = new Map<string, number>();

/**
 * Check if an error message matches a rate-limited error type
 */
function getRateLimitType(message: string): string | null {
  if (message.includes('InvalidJWTToken: Invalid value for JWT claim')) {
    return 'JWT_CLAIM';
  }
  return null;
}


/**
 * Check if we should log an error based on rate limiting
 */
function shouldLogError(errorType: string): boolean {
  const now = Date.now();
  const lastLogTime = lastErrorLogTimes.get(errorType) || 0;
  const timeSinceLastLog = now - lastLogTime;
  
  if (timeSinceLastLog >= (ERROR_RATE_LIMITS[errorType as keyof typeof ERROR_RATE_LIMITS] || 0)) {
    lastErrorLogTimes.set(errorType, now);
    return true;
  }
  return false;
}

/**
 * Safely formats log details into a string representation, handling various types and edge cases
 * 
 * Handles:
 * - Objects: Pretty prints with JSON if possible, falls back to toString()
 * - Primitives: Converts to string if non-empty
 * - Empty values: Returns empty string
 * - Error cases: Returns error indicator if formatting fails
 * 
 * @param details - The details to format, can be any type
 * @returns Formatted string, prefixed with a space if non-empty
 */
function formatLogDetails(details: any): string {
  try {
    if (details !== null && typeof details === 'object') {
      try {
        // For objects, attempt pretty print with indentation
        const stringified = JSON.stringify(details, null, 2);
        if (stringified !== '{}') {
          return ` ${stringified}`;
        }
      } catch (jsonError) {
        // If JSON stringify fails (e.g., circular references), fallback to basic string conversion
        const fallbackString = String(details);
        if (fallbackString !== '[object Object]' && fallbackString !== '') {
          return ` ${fallbackString}`;
        }
      }
    } else if (details !== undefined && details !== null && details !== '') {
      // For non-empty primitives, append as string
      return ` ${String(details)}`;
    }
    return ''; // For empty values (undefined, null, empty string, empty object)
  } catch (detailsError) {
    // If all formatting attempts fail, add a note about the failure
    console.error('Logger: Failed to format details:', detailsError);
    return ' [Error formatting details]';
  }
}

export interface ErrorWithMessage {
  message: string
  stack?: string
  code?: string | number
  [key: string]: any
}

function isErrorWithMessage(error: unknown): error is ErrorWithMessage {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as Record<string, unknown>).message === 'string'
  )
}

function toErrorWithMessage(maybeError: unknown): ErrorWithMessage {
  if (isErrorWithMessage(maybeError)) return maybeError

  try {
    return new Error(JSON.stringify(maybeError))
  } catch {
    // fallback in case there's an error stringifying the maybeError
    // like with circular references for example
    return new Error(String(maybeError))
  }
}

export const useLogger = () => {
  const level = ref(LOG_LEVELS.DEBUG);

  /**
   * Our internal logging facility. Ensures good logging in all environments
   * @param logLevel - The level of the log, one of the keys of LOG_LEVELS
   * @param message - The message to log
   * @param details - Any additional details to log. Can be any type - will be stringified appropriately. A dict with keys and values is prefered for good readability of the logged data
   */
  const log = async (logLevel: keyof typeof LOG_LEVELS, message: string, details: any = {}) => {
    try {
      if (LOG_LEVELS[logLevel] >= level.value) {
        const formattedDetails = formatLogDetails(details);
        const logMessage = `NSTRAPP: [${logLevel}] ${message} ${formattedDetails}`;

        if (Capacitor.isNativePlatform()) {
          try {
            switch (logLevel) {
              case 'DEBUG':
                console.debug(logMessage);
                break;
              case 'INFO':
                console.info(logMessage);
                break;
              case 'WARN':
                console.warn(logMessage);
                break;
              case 'ERROR':
                console.error(logMessage);
                break;
            }
          } catch (nativeError) {
            console.error(`Failed to log: ${logMessage}`);
          }
        } else {
          // normal console logging
          console[logLevel.toLowerCase()](logMessage);
        }
      }
    } catch (error) {
      try {
        console.error('Logger failed:', error);
      } catch {
        // Silent fail if even the error logging fails
      }
    }
  };

  /**
   * Converts any error type to a standardized error object with a message property
   * @param error - The error to be converted, can be of any type
   * @returns An object with at least a message property describing the error
   */
  const getErrorMessage = (error: unknown) => {
    return toErrorWithMessage(error).message
  }

  const debug = (message: string, details?: any) => {
    log('DEBUG', message, details)
  }

  const info = (message: string, details?: any) => {
    log('INFO', message, details)
  }

  const warn = (message: string, details?: any) => {
    log('WARN', message, details)
  }

  /**
   * Logs an error to the console and Sentry in a non-error catch situation
   * @param message - The message to log
   * @param details - Any additional details to log. Can be any type - will be stringified appropriately. A dict with keys and values is prefered for good readability of the logged data
   */ 
  const error = (message: string, details?: any) => {
    log('ERROR', message, details)
    logIssueToSentry(message, message, 'error', details)
  }

  /**
   * Logs an error to the console and Sentry in a catch situation where the error catch is inside our own logic
   * @param message - The message of the error from our logic. 
   * @param error - The caught error to log
   * @param details - Any additional details to log. Can be any type - will be stringified appropriately. A dict with keys and values is prefered for good readability of the logged data
   * @param context - A string describing the context of the error, such as the component or function name
   */
  const logErrorCaught = (message: string, context: string, error: unknown, details?: any) => {
    const detailsToLog = {
      message: message,
      ...details
    }
    // debug("In logErrorCaught",  { message: message, context: context, error: error, details: detailsToLog })
    handleError(error, context, detailsToLog)
  }

  /**
   * Used for throwing an error when a Supabase error is encountered and should be thrown.
   * Key feature here is that this method ensures that the call stack is preserved in the error object
   * which it is not if the error is thrown directly as throw(supabase_error_object)
   * @param error - The Supabase error object to throw
   * @param message - An optional additional message to include in the error logging
   */
  const throwSupabaseError = (error: any, message: string | undefined = undefined) => {
    let logMsg = "Supabase Error thrown"
    if (message) {
      logMsg = logMsg + ": " + message
    }
    if (error.message) {
      logMsg = logMsg + ": " + error.message
    }
    const enhancedError = new Error(logMsg);
    throw enhancedError;
  }
  
  /**
   * Used when the Vue system catches an error and has little context other than where in the execution flow it occurred
   * Logs to both the console and Sentry
   * @param error - The error found - can be any type, will be converted to a standardized error object and then logged
   * @param context - A string describing the context of the error, such as the component or function name
   */
  const handleError = (error: unknown, context: string, details?: any) => {
    const errorWithMessage = toErrorWithMessage(error)
    
    // Log the error with context using our own logger facility 
    log('ERROR',
      `Error in: ${context}`,
      {
        message: errorWithMessage.message,
        stack: errorWithMessage.stack,
        code: errorWithMessage.code,
        context: context,
        details: details
      }
    )

    // Check for rate limiting
    const rateLimitType = getRateLimitType(errorWithMessage.message);
    if (rateLimitType) {
      if (!shouldLogError(rateLimitType)) {
        // Still log locally but skip Sentry
        return;
      }
    }

    // Log to Sentry with additional context
    Sentry.captureException(error, {
      tags: {
        errorContext: context || 'unknown'
      },
      extra: {
        originalError: error,
        parsedError: errorWithMessage
      }
    })
  }

  /**
   * Logs a problematic situation that isn't an error to Sentry
   * Used internally to log issues that are identified in code, but are not thrown errors.
   * For thrown errors, use the logErrorCaught function which will also include stack trace and other context.
   * @param message - A clear description of the issue
   * @param context - Where in the code this is happening (e.g., component name, function name)
   * @param level - The severity level of the issue (defaults to 'warning')
   * @param details - Additional structured data about the issue
   */
  const logIssueToSentry = (
    message: string, 
    context: string,
    level: 'debug' | 'info' | 'warning' | 'error' = 'warning',
    details: any = {}
  ) => {
    // Log locally first with combined details
    const combinedDetails = {
      context,
      ...details
    };

    // normal logging is not handled here, only Sentry
    // const logLevel = level.toUpperCase() as keyof typeof LOG_LEVELS;
    // log(logLevel, message, combinedDetails);

    // Then log to Sentry as a non-error issue
    Sentry.captureMessage(message, {
      level,
      tags: {
        issueContext: context
      },
      extra: {
        ...details,
        timestamp: new Date().toISOString()
      }
    })
  }

  return {
    debug,
    info,
    warn,
    error,
    logErrorCaught,
    handleError,
    throwSupabaseError
  };
}; 